import {
  columnFacetingFeature,
  columnFilteringFeature,
  columnVisibilityFeature,
  createColumnHelper,
  createFacetedMinMaxValues,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createSortedRowModel,
  filterFns,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSortingFeature,
  sortFns,
  tableFeatures,
  type AccessorKeyColumnDef,
  type AccessorKeyColumnDefBase,
  type CellContext,
  type Column,
  type ColumnDef,
  type Row,
  type RowData
} from '@tanstack/react-table';

/**
 * The single feature registry shared by every Efacity table.
 *
 * TanStack Table v9 no longer bundles every feature: each feature has to be
 * registered explicitly, and the resulting object is the type parameter that
 * carries the feature-gated APIs through `Column`, `ColumnDef`, `Row`, etc.
 * Because the whole app renders its tables through the single `Table`
 * component below, one shared registry is enough - keep it in sync with the
 * APIs `Table` and the column filters actually use:
 *
 * - `columnFilteringFeature` - `column.getCanFilter/getFilterValue/setFilterValue`
 * - `columnFacetingFeature` - `column.getFacetedMinMaxValues/getFacetedUniqueValues`
 * - `columnVisibilityFeature` - `row.getVisibleCells()`
 * - `rowExpandingFeature` - `row.getIsExpanded()` for `renderRowSubComponent`
 * - `rowPaginationFeature` - `pagination` state and `table.setPageSize()`
 * - `rowSortingFeature` - `column.getCanSort/getIsSorted/getToggleSortingHandler`
 *
 * The `filterFns`/`sortFns` registries are spread in full so that columns
 * keep the v8 `auto` filter/sort resolution for client-side tables
 * (`manualFiltering={false}` / `manualSorting={false}`).
 *
 * No `facetedRowModel` factory is registered on purpose: faceted values are
 * then derived from the pre-filtered row model, which is what v8 did when
 * `getFacetedRowModel` was omitted.
 */
export const efacityTableFeatures = tableFeatures({
  columnFacetingFeature,
  columnFilteringFeature,
  columnVisibilityFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSortingFeature,
  facetedMinMaxValues: createFacetedMinMaxValues(),
  facetedUniqueValues: createFacetedUniqueValues(),
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  filterFns,
  sortFns
});

export type EfacityTableFeatures = typeof efacityTableFeatures;

/** `Column` bound to the shared Efacity feature registry. */
export type TableColumn<TData extends RowData, TValue = unknown> = Column<EfacityTableFeatures, TData, TValue>;

/** `ColumnDef` bound to the shared Efacity feature registry. */
export type TableColumnDef<TData extends RowData, TValue = unknown> = ColumnDef<EfacityTableFeatures, TData, TValue>;

/**
 * Column definitions as accepted by the `Table` component. Mirrors the
 * `TableOptions['columns']` shape, whose `any` value type is what lets a list
 * mix accessor columns of different value types.
 */
export type TableColumnDefs<TData extends RowData> = TableColumnDef<TData, any>[];

/** `AccessorKeyColumnDef` bound to the shared Efacity feature registry. */
export type TableAccessorKeyColumnDef<TData extends RowData, TValue = unknown> = AccessorKeyColumnDef<
  EfacityTableFeatures,
  TData,
  TValue
>;

/** `AccessorKeyColumnDefBase` bound to the shared Efacity feature registry. */
export type TableAccessorKeyColumnDefBase<TData extends RowData, TValue = unknown> = AccessorKeyColumnDefBase<
  EfacityTableFeatures,
  TData,
  TValue
>;

/** `CellContext` bound to the shared Efacity feature registry. */
export type TableCellContext<TData extends RowData, TValue = unknown> = CellContext<
  EfacityTableFeatures,
  TData,
  TValue
>;

/** `Row` bound to the shared Efacity feature registry. */
export type TableRowModel<TData extends RowData> = Row<EfacityTableFeatures, TData>;

/**
 * `createColumnHelper` pre-bound to the shared feature registry, so callers
 * keep the v8-style `createTableColumnHelper<MyRow>()` call shape.
 */
export const createTableColumnHelper = <TData extends RowData>() => createColumnHelper<EfacityTableFeatures, TData>();
